'use client';
import { jsxs, jsx } from 'react/jsx-runtime';
import classNames from 'classnames';
import useEmblaCarousel from '../../../../node_modules/embla-carousel-react/esm/embla-carousel-react.esm.js';
import { useState, useCallback, useEffect } from 'react';
import FocusLockCombination from '../../../../node_modules/react-focus-lock/dist/es2015/Combination.js';
import flattenChildren from '../../../../node_modules/react-keyed-flatten-children/dist/index.js';
import { AngleLeft, AngleRight } from '@zooplus/icons';
import { getTestLabels } from '../../../../utils/getTestLabels.js';
import '../../../Button/Button.js';
import { IconButton } from '../../../Button/IconButton.js';
import { SlideWrap } from '../SlideWrap.js';
import style from './Lightbox.module.scss.js';

const { componentTestLabel, elementTestLabel } = getTestLabels('LightboxBeta');
const Lightbox = (props) => {
    const { children, className, ariaLabel, closeButtonAriaLabel, handleKeyDown, initialSlide, isOpen, nextButtonAriaLabel, onClose, onSlideChange, previousButtonAriaLabel, title, ...otherProps } = props;
    const slides = flattenChildren(children);
    const [emblaRef, emblaApi] = useEmblaCarousel({
        duration: 16.5,
        slidesToScroll: 1,
    });
    const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
    const [nextButtonDisabled, setnextButtonDisabled] = useState(true);
    const [selectedIndex, setSelectedIndex] = useState(initialSlide);
    const [isFirstInit, setIsFirstInit] = useState(true);
    const scrollPrev = useCallback(() => {
        onSlideChange?.(selectedIndex - 1);
        return emblaApi && emblaApi.scrollPrev();
    }, [emblaApi, onSlideChange, selectedIndex]);
    const scrollNext = useCallback(() => {
        onSlideChange?.(selectedIndex + 1);
        return emblaApi && emblaApi.scrollNext();
    }, [emblaApi, onSlideChange, selectedIndex]);
    const onInit = useCallback((emblaApi) => {
        if (isFirstInit) {
            emblaApi.scrollTo(initialSlide, true);
            setIsFirstInit(false);
        }
    }, [initialSlide, isFirstInit]);
    const onKeyDown = (evt) => {
        if (evt.key === 'ArrowRight') {
            scrollNext();
        }
        if (evt.key === 'ArrowLeft') {
            scrollPrev();
        }
        handleKeyDown(evt);
    };
    const onSelect = useCallback((emblaApi) => {
        if (emblaApi) {
            setSelectedIndex(emblaApi.selectedScrollSnap());
            setPrevBtnDisabled(!emblaApi.canScrollPrev());
            setnextButtonDisabled(!emblaApi.canScrollNext());
        }
    }, [setSelectedIndex]);
    useEffect(() => {
        if (!emblaApi)
            return;
        onInit(emblaApi);
        onSelect(emblaApi);
        emblaApi.on('select', onSelect);
    }, [emblaApi, onInit, onSelect]);
    const lightbox = (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions
    jsxs("div", { "data-zta": componentTestLabel, 
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex: 0, onKeyDown: onKeyDown, ...otherProps, className: classNames(style['z-lightbox-beta'], {
            [style['z-lightbox-beta--open']]: isOpen,
        }, className), role: "dialog", "aria-modal": "true", "aria-label": ariaLabel, children: [isOpen && jsx("style", { children: `body { overflow: hidden; }` }), jsxs("div", { className: style['z-lightbox-beta__body'], style: {
                    '--pp-CarouselBeta-slide-spacing': 0,
                    '--pp-CarouselBeta-slide-size': '100%',
                }, children: [jsxs("span", { className: style['z-lightbox-beta__counter'], children: [selectedIndex + 1, " / ", slides.length] }), jsx(IconButton, { "data-autofocus": true, className: style['z-lightbox-beta__close-btn'], "data-zta": elementTestLabel('CloseButton'), onClick: onClose, variant: "inverse", ariaLabel: closeButtonAriaLabel }), jsx("div", { "data-zta": elementTestLabel('SlideTrack'), className: style['z-lightbox-beta__slide-track'], ref: emblaRef, children: jsx("div", { className: style['z-lightbox-beta__slide-track-inner'], children: slides.map((item, index) => (jsx(SlideWrap, { isInert: emblaApi?.selectedScrollSnap() !== index, children: item }, index))) }) }), jsx(IconButton, { "data-zta": elementTestLabel('PreviousButton'), className: classNames(style['z-lightbox-beta__arrow'], style['z-lightbox-beta__arrow--prev']), onClick: scrollPrev, disabled: prevBtnDisabled, variant: "inverse", ariaLabel: previousButtonAriaLabel, children: jsx(AngleLeft, { "aria-hidden": true }) }), jsx(IconButton, { "data-zta": elementTestLabel('NextButton'), className: classNames(style['z-lightbox-beta__arrow'], style['z-lightbox-beta__arrow--next']), onClick: scrollNext, disabled: nextButtonDisabled, variant: "inverse", ariaLabel: nextButtonAriaLabel, children: jsx(AngleRight, { "aria-hidden": true }) })] }), jsx("div", { "data-zta": elementTestLabel('Footer'), className: style['z-lightbox-beta__footer'], children: title })] }));
    return (jsx(FocusLockCombination, { returnFocus: true, 
        // eslint-disable-next-line jsx-a11y/no-autofocus
        autoFocus: true, disabled: !isOpen, className: style['z-lightbox-beta-focus-lock'], children: lightbox }));
};

export { Lightbox };
//# sourceMappingURL=Lightbox.js.map
